import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { ChatbotService } from '@src/app/core/services/chatbot/chatbot.service';
import { environment } from 'src/environments/environment';
import { ChatbotPositionHelper, ChatbotResizeHelper } from './chatbot.utils';
import { TranslateModule } from '@ngx-translate/core';

interface DirectLineToken {
  token: string;
  conversationId?: string;
}

declare global {
  interface Window {
    WebChat: any;
  }
}

@Component({
  selector: 'app-chatbot-component',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: './chatbot.component.html',
  styleUrl: './chatbot.component.scss',
})
export class ChatbotComponent implements OnInit, OnDestroy {
  @ViewChild('webchat', { static: false }) webchatContainer!: ElementRef;

  // Estado del chat
  open = false;
  minimized = false;

  // Dimensiones y posición
  chatWidth = 410;
  chatHeight = Math.min(window.innerHeight * 0.6, 700);
  chatPosX = window.innerWidth - 410 - 32;
  chatPosY = window.innerHeight - this.chatHeight - 16;

  // Estados de interacción
  private dragging = false;
  private resizing = false;
  private resizingDir = '';

  // Coordenadas de inicio para drag/resize
  private startX = 0;
  private startY = 0;
  private startWidth = 0;
  private startHeight = 0;
  private startPosX = 0;
  private startPosY = 0;

  // Configuración de WebChat
  scriptUrl = environment.webchatScriptUrl;
  domain = environment.webchatDomain;

  constructor(private chatbotService: ChatbotService) {}

  ngOnInit() {
    window.addEventListener('resize', this.onWindowResize);
  }

  ngOnDestroy() {
    window.removeEventListener('resize', this.onWindowResize);
  }

  onWindowResize = () => {
    if (!this.open) return;

    const adjusted = ChatbotPositionHelper.adjustOnWindowResize({
      width: this.chatWidth,
      height: this.chatHeight,
      posX: this.chatPosX,
      posY: this.chatPosY,
    });

    this.chatWidth = adjusted.width;
    this.chatHeight = adjusted.height;
    this.chatPosX = adjusted.posX;
    this.chatPosY = adjusted.posY;
  };

  async loadWebChat() {
    this.open = true;

    const scriptId = 'webchat-script';
    if (!document.getElementById(scriptId)) {
      const script = document.createElement('script');
      script.id = scriptId;
      script.src = this.scriptUrl;
      script.onload = () => this.renderWebChat();
      document.body.appendChild(script);
    } else {
      this.renderWebChat();
    }
  }

  private createCustomStore() {
    return window.WebChat.createStore(
      {},
      ({ dispatch }: { dispatch: (action: any) => void }) =>
        (next: any) =>
        (action: any) => {
          if (action.type === 'DIRECT_LINE/CONNECT_FULFILLED') {
            dispatch({
              type: 'DIRECT_LINE/POST_ACTIVITY',
              meta: { method: 'keyboard' },
              payload: {
                activity: {
                  channelData: { postBack: true },
                  name: 'startConversation',
                  type: 'event',
                },
              },
            });
          }
          return next(action);
        },
    );
  }

  private renderWebChat() {
    if (this.webchatContainer?.nativeElement) {
      this.webchatContainer.nativeElement.innerHTML = '';
    }

    this.chatbotService.getChatbotAuthToken().subscribe({
      next: (res: DirectLineToken) => {
        const directLine = window.WebChat.createDirectLine({
          domain: this.domain,
          token: res.token,
        });

        window.WebChat.renderWebChat(
          {
            directLine,
            styleOptions: {
              botAvatarImage: 'https://cdn.aunoa.ai/804/icons/aire_perfil.png',
              botAvatarInitials: '',
            },
            store: this.createCustomStore(),
          },
          this.webchatContainer.nativeElement,
        );
      },
      error: (err: any) => {
        console.error('No se pudo obtener el token de Direct Line', err);
      },
    });
  }
  minimizarChat() {
    this.minimized = true;
  }

  // ============================================
  // DRAG & DROP - Movimiento desde cabecera
  // ============================================
  onHeaderMouseDown(event: MouseEvent) {
    event.preventDefault();
    this.dragging = true;
    this.startX = event.clientX;
    this.startY = event.clientY;
    this.startPosX = this.chatPosX;
    this.startPosY = this.chatPosY;
    document.addEventListener('mousemove', this.onHeaderMouseMove);
    document.addEventListener('mouseup', this.onHeaderMouseUp);
  }

  private onHeaderMouseMove = (event: MouseEvent) => {
    if (!this.dragging) return;

    const newX = this.startPosX + (event.clientX - this.startX);
    const newY = this.startPosY + (event.clientY - this.startY);

    const constrained = ChatbotPositionHelper.constrainPosition(newX, newY, {
      width: this.chatWidth,
      height: this.chatHeight,
      posX: this.chatPosX,
      posY: this.chatPosY,
    });

    this.chatPosX = constrained.x;
    this.chatPosY = constrained.y;
  };

  private onHeaderMouseUp = () => {
    this.dragging = false;
    document.removeEventListener('mousemove', this.onHeaderMouseMove);
    document.removeEventListener('mouseup', this.onHeaderMouseUp);
  };

  // ============================================
  // RESIZE - Redimensión desde bordes/esquinas
  // ============================================
  onResizeMouseDown(event: MouseEvent, dir: string) {
    event.stopPropagation();
    this.resizing = true;
    this.resizingDir = dir;
    this.startX = event.clientX;
    this.startY = event.clientY;
    this.startWidth = this.chatWidth;
    this.startHeight = this.chatHeight;
    this.startPosX = this.chatPosX;
    this.startPosY = this.chatPosY;
    document.addEventListener('mousemove', this.onResizeMouseMove);
    document.addEventListener('mouseup', this.onResizeMouseUp);
  }

  private onResizeMouseMove = (event: MouseEvent) => {
    if (!this.resizing) return;

    const dx = event.clientX - this.startX;
    const dy = event.clientY - this.startY;
    const constraints = ChatbotResizeHelper.getConstraints(
      this.startPosX,
      this.startPosY,
    );

    switch (this.resizingDir) {
      case 'right':
        this.chatWidth = ChatbotResizeHelper.resizeRight(
          this.startWidth,
          dx,
          constraints,
        );
        break;

      case 'left': {
        const result = ChatbotResizeHelper.resizeLeft(
          this.startWidth,
          this.startPosX,
          dx,
          constraints,
        );
        this.chatWidth = result.width;
        this.chatPosX = result.posX;
        break;
      }

      case 'bottom':
        this.chatHeight = ChatbotResizeHelper.resizeBottom(
          this.startHeight,
          dy,
          constraints,
        );
        break;

      case 'top': {
        const result = ChatbotResizeHelper.resizeTop(
          this.startHeight,
          this.startPosY,
          dy,
          constraints,
        );
        this.chatHeight = result.height;
        this.chatPosY = result.posY;
        break;
      }

      case 'top-left': {
        const horizontal = ChatbotResizeHelper.resizeLeft(
          this.startWidth,
          this.startPosX,
          dx,
          constraints,
        );
        this.chatWidth = horizontal.width;
        this.chatPosX = horizontal.posX;

        const vertical = ChatbotResizeHelper.resizeTop(
          this.startHeight,
          this.startPosY,
          dy,
          constraints,
        );
        this.chatHeight = vertical.height;
        this.chatPosY = vertical.posY;
        break;
      }

      case 'top-right': {
        this.chatWidth = ChatbotResizeHelper.resizeRight(
          this.startWidth,
          dx,
          constraints,
        );

        const vertical = ChatbotResizeHelper.resizeTop(
          this.startHeight,
          this.startPosY,
          dy,
          constraints,
        );
        this.chatHeight = vertical.height;
        this.chatPosY = vertical.posY;
        break;
      }

      case 'bottom-left': {
        const horizontal = ChatbotResizeHelper.resizeLeft(
          this.startWidth,
          this.startPosX,
          dx,
          constraints,
        );
        this.chatWidth = horizontal.width;
        this.chatPosX = horizontal.posX;

        this.chatHeight = ChatbotResizeHelper.resizeBottom(
          this.startHeight,
          dy,
          constraints,
        );
        break;
      }

      case 'bottom-right':
        this.chatWidth = ChatbotResizeHelper.resizeRight(
          this.startWidth,
          dx,
          constraints,
        );
        this.chatHeight = ChatbotResizeHelper.resizeBottom(
          this.startHeight,
          dy,
          constraints,
        );
        break;
    }
  };

  private onResizeMouseUp = () => {
    this.resizing = false;
    document.removeEventListener('mousemove', this.onResizeMouseMove);
    document.removeEventListener('mouseup', this.onResizeMouseUp);
  };

  // ============================================
  // CHAT - Abrir/Cerrar
  // ============================================

  abrirChat() {
    if (this.open && this.minimized) {
      this.minimized = false;
      return;
    }

    // Restaurar dimensiones y posición iniciales
    const initial = ChatbotPositionHelper.getInitialDimensions(410);
    this.chatHeight = initial.height;
    this.chatPosX = initial.posX;
    this.chatPosY = initial.posY;

    this.minimized = false;
    this.open = true;
    this.loadWebChat();
  }

  cerrarChat() {
    this.open = false;
    this.minimized = false;

    // Restaurar dimensiones iniciales
    const initial = ChatbotPositionHelper.getInitialDimensions(410);
    this.chatPosX = initial.posX;
    this.chatPosY = initial.posY;
    this.chatWidth = 410;
    this.chatHeight = initial.height;

    // Limpiar contenedor
    if (this.webchatContainer?.nativeElement) {
      this.webchatContainer.nativeElement.innerHTML = '';
    }
  }
}
